/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, Center, Divider, Group, Loader, MultiSelect, Switch, Text } from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { FC, useEffect, useState } from 'react';
import { i18n } from '@lingui/core';
import { useForm } from '@mantine/form';
import { IconChevronDown } from '@tabler/icons';
import { GoogleCalendarDto, VisibilityScope } from '../../../../Types/GoogleT';
import Dot from '../../../../Atoms/Dot';
import ConfigOption from '../../Google/ConfigOption';
import { GoogleS } from '../../../../Service/GoogleS';
import { LicenceRestriction, LicenceS } from '../../../../Service/LicenceS';
import { MixpanelS } from '../../../../Service/MixpanelS';
import { ConstantS } from '../../../../Service/ConstantS';
import GrayBox from '../../../../Atoms/GrayBox';
import { CustomAttributeS } from '../../../../Service/Context/CustomAttributeS';
import { DataS } from '../../../../Service/DataS';
import { CustomAttributeWithGroupNameDTO } from '../../../../Types/CustomAttributeT';
import BookingState from '../../../../Types/Enums/BookingState';
import { CustomEventState } from '../../../../Types/EventT';
import { EventS } from '../../../../Service/EventS';
import { useEventKinds } from '../../../../Service/hooks/useEventKinds';
import { useEventStates } from '../../../../Service/hooks/useEventStates';
import { ThemeText } from '../../../../Atoms/ThemeText';

// eslint-disable-next-line import/prefer-default-export
export const openGoogleCalendarConfigModal = (
  googleCalendar: GoogleCalendarDto,
  updateHandler: (patch: Partial<GoogleCalendarDto>) => void,
  checkLicence: (licenceRestriction: LicenceRestriction, featureHandler: () => unknown) => void,
) => {
  openModal({
    size: 'lg',
    title: (
      <Text weight="bolder" size="xl">
        🗓️ Kalenderoptionen
      </Text>
    ),
    closeOnEscape: true,
    transition: 'slide-down',
    children: (
      <GoogleCalendarConfigModal
        googleCalendar={googleCalendar}
        updateHandler={updateHandler}
        checkLicence={checkLicence}
      />
    ),
  });
};

export const openGoogleCalendarAdvancedConfigModal = (
  googleCalendar: GoogleCalendarDto,
  updateHandler: (patch: Partial<GoogleCalendarDto>) => void,
) => {
  openModal({
    size: 'lg',
    title: (
      <Text weight="bolder" size="xl">
        🗓️ Erweiterte Einstellungen
      </Text>
    ),
    closeOnEscape: true,
    transition: 'slide-down',
    children: <GoogleCalendarAdvancedConfigModal googleCalendar={googleCalendar} updateHandler={updateHandler} />,
  });
};

interface ConfigModalProps {
  googleCalendar: GoogleCalendarDto;
  updateHandler: (patch: Partial<GoogleCalendarDto>) => void;
  checkLicence: (licenceRestriction: LicenceRestriction, featureHandler: () => unknown) => void;
}

interface AdvancedConfigModalProps {
  googleCalendar: GoogleCalendarDto;
  updateHandler: (patch: Partial<GoogleCalendarDto>) => void;
}

const calendarBookingStates = [BookingState.OPEN, BookingState.OFFERED, BookingState.BOOKED];

const isCalendarBookingState = (state: BookingState): boolean => calendarBookingStates.includes(state);

const getCustomStateOptions = (states: CustomEventState[]) =>
  EventS.sortCustomStates(states)
    .filter((state) => !!state.id && isCalendarBookingState(state.originState))
    .map((state) => ({ value: state.id as string, label: state.label }));

const getCalendarBookingStateOptions = () =>
  DataS.getBookingStateOptions().filter(({ value }) => isCalendarBookingState(value));

const filterCalendarBookingStates = (states: BookingState[]) => states.filter(isCalendarBookingState);

const visibilityScopeOptions = [
  {
    value: VisibilityScope.CUSTOMER_DATA,
    title: 'Kundendaten anzeigen',
    description: 'Kalendereinträge enthalten Kundendaten wie Name, Firma und Kontaktdaten.',
  },
  {
    value: VisibilityScope.EVENT_NOTES,
    title: 'Notizen anzeigen',
    description: 'Kalendereinträge enthalten Deine Event-Notizen.',
  },
  {
    value: VisibilityScope.EVENT_KIND,
    title: 'Auftragsart anzeigen',
    description: 'Kalendereinträge enthalten die Auftragsart der Buchung.',
  },
  {
    value: VisibilityScope.EVENT_STATE,
    title: 'Buchungsstatus anzeigen',
    description: 'Kalendereinträge enthalten den Buchungsstatus der Buchung.',
  },
  {
    value: VisibilityScope.EVENT_NAME,
    title: 'Event-Name anzeigen',
    description: 'Kalendereinträge enthalten den Namen der Buchung.',
  },
  {
    value: VisibilityScope.EVENT_DESCRIPTION,
    title: 'Event-Beschreibung anzeigen',
    description: 'Kalendereinträge enthalten die Beschreibung der Buchung.',
  },
  {
    value: VisibilityScope.LOCATION,
    title: 'Location anzeigen',
    description: 'Kalendereinträge enthalten die hinterlegte Location und Adresse.',
  },
];

const onlyLocationCityVisibilityScopeOption = {
  value: VisibilityScope.ONLY_LOCATION_CITY,
  title: 'Nur Stadt der Location anzeigen',
  description:
    'Kalendereinträge enthalten nur die Stadt der Location. Straße, Hausnummer und Postleitzahl werden ausgeblendet.',
};

const GoogleCalendarConfigModal: FC<ConfigModalProps> = ({ googleCalendar, updateHandler, checkLicence }) => {
  const {
    id,
    backgroundColor,
    calendarName,
    read,
    checkForClashes,
    write,
    recommendation,
    insertOnlySharedRecommendations,
    insertAsFullTimeEvents,
    useCustomStateLabels,
    useEventDateDescription,
  } = googleCalendar;
  const form = useForm({
    initialValues: {
      read,
      checkForClashes,
      write,
      recommendation,
      insertOnlySharedRecommendations,
      insertAsFullTimeEvents,
      useCustomStateLabels,
      useEventDateDescription,
    },
  });
  const [loading, setLoading] = useState(false);

  const {
    read: readValue,
    checkForClashes: checkForClashesValue,
    write: writeValue,
    recommendation: recommendationValue,
  } = form.values;

  useEffect(() => {
    if (form.isDirty() && readValue && (writeValue || recommendationValue)) {
      form.setValues({ write: false, recommendation: false });
    }

    if (form.isDirty() && !readValue) {
      form.setValues({ checkForClashes: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readValue]);

  useEffect(() => {
    if (form.isDirty() && writeValue && (readValue || checkForClashesValue)) {
      form.setValues({ read: false, checkForClashes: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writeValue]);

  useEffect(() => {
    if (form.isDirty() && checkForClashesValue && !readValue) {
      form.setValues({ read: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkForClashesValue]);

  useEffect(() => {
    if (form.isDirty() && recommendationValue) {
      form.setValues({ write: false, read: false, checkForClashes: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendationValue]);

  const onSubmit = () => {
    if (form.isDirty('write') && form.values.write) {
      MixpanelS.track(ConstantS.TrackingEvents.GoogleCalendarActivated);
    }
    setLoading(true);
    GoogleS.editCalendarConfig(id, form.values)
      .then((calendar) => {
        if (calendar) {
          updateHandler(calendar);
          closeAllModals();
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Group>
        <Text>Kalender:</Text>
        <Group mb="md" mt="md">
          <Dot withoutShadow colour={backgroundColor} />
          <Text weight="bolder">{calendarName}</Text>
        </Group>
      </Group>
      <GrayBox>
        <ConfigOption
          title="bookitup Events eintragen"
          description="Es werden automatisch bookitup Termine in Deinen Kalender eingetragen, welche im Status Angefragt, Option oder Bestätigt sind."
          rightSection={<Switch {...form.getInputProps('write', { type: 'checkbox' })} />}
        />
        {writeValue && (
          <ConfigOption
            title="als Ganztags-Event eintragen"
            description="Die Kalender-Einträge werden ganztags eingetragen."
            rightSection={
              <Switch {...form.getInputProps('insertAsFullTimeEvents', { type: 'checkbox' })} disabled={readValue} />
            }
          />
        )}
        {writeValue && (
          <ConfigOption
            title="Eigenen Buchungsstatus verwenden"
            description="Die Kalender-Einträge enthalten deinen individuell erstellen Buchungsstatus."
            rightSection={
              <Switch {...form.getInputProps('useCustomStateLabels', { type: 'checkbox' })} disabled={readValue} />
            }
          />
        )}
        {writeValue && (
          <ConfigOption
            title="Event-Tag-Beschreibung verwenden"
            description="Die Kalender-Einträge enthalten die Beschreibung vom Event-Tag."
            rightSection={
              <Switch {...form.getInputProps('useEventDateDescription', { type: 'checkbox' })} disabled={readValue} />
            }
          />
        )}
        <ConfigOption
          title="Termine anzeigen"
          description="bookitup zeigt Dir in der Kalender-Ansicht die Termine dieses Kalenders an."
          rightSection={
            <Switch
              checked={form.values.read}
              onChange={(e) =>
                checkLicence(LicenceS.Restrictions.PRIVATE_CALENDAR, () =>
                  form.setFieldValue('read', e.currentTarget.checked),
                )
              }
            />
          }
        />
        <ConfigOption
          title="Termine prüfen"
          description="bookitup prüft diesen Kalander auf Terminkollisionen."
          rightSection={
            <Switch
              checked={form.values.checkForClashes}
              disabled={writeValue}
              onChange={(e) =>
                checkLicence(LicenceS.Restrictions.PRIVATE_CALENDAR, () =>
                  form.setFieldValue('checkForClashes', e.currentTarget.checked),
                )
              }
            />
          }
        />
        <ConfigOption
          title="Vermittlungen eintragen"
          description="Es werden automatisch alle Vermittlungen eingetragen, bei denen Du einen anderen Dienstleister vermittelt hast."
          rightSection={<Switch {...form.getInputProps('recommendation', { type: 'checkbox' })} />}
        />
        {recommendationValue && (
          <ConfigOption
            title="Vermittlungen nur eintragen, wenn Auftragsdaten geteilt wurden"
            description="Ist diese Option deaktiviert, werden alle Vermittlungen eingetragen - egal ob die Auftragsdaten geteilt wurden oder nicht."
            rightSection={
              <Switch
                disabled={!form.values.recommendation || readValue}
                {...form.getInputProps('insertOnlySharedRecommendations', { type: 'checkbox' })}
              />
            }
          />
        )}
      </GrayBox>
      <Center mt="xl">
        <Button sx={{ minWidth: 200 }} type="submit" disabled={loading || !form.isTouched()}>
          {i18n._('actions.save')}
        </Button>
      </Center>
    </form>
  );
};

const GoogleCalendarAdvancedConfigModal: FC<AdvancedConfigModalProps> = ({ googleCalendar, updateHandler }) => {
  const {
    id,
    backgroundColor,
    calendarName,
    customFields,
    visibilityScopes,
    statesOfInterest,
    customStatesOfInterest,
    kindsOfInterest,
  } = googleCalendar;
  const form = useForm({
    initialValues: {
      customFields: customFields ?? [],
      visibilityScopes: visibilityScopes ?? [],
      statesOfInterest: statesOfInterest ?? [],
      customStatesOfInterest: customStatesOfInterest ?? [],
      kindsOfInterest: kindsOfInterest ?? [],
    },
  });
  const [loading, setLoading] = useState(false);
  const { loading: eventStatesLoading, states: eventStates } = useEventStates();
  const { loading: eventKindsLoading, kinds } = useEventKinds();
  const [customAttributesLoading, setCustomAttributesLoading] = useState(true);
  const [customAttributesLoaded, setCustomAttributesLoaded] = useState(false);
  const [customAttributes, setCustomAttributes] = useState<CustomAttributeWithGroupNameDTO[]>([]);

  const customAttributeOptions = DataS.getCustomAttributeOptionsV2(customAttributes);
  const allowedCustomFieldNames = customAttributeOptions.map(({ value }) => value);
  const customFieldsValue = customAttributesLoaded
    ? form.values.customFields.filter((fieldName) => allowedCustomFieldNames.includes(fieldName))
    : form.values.customFields;
  const bookingStateOptions = getCalendarBookingStateOptions();
  const statesOfInterestValue = filterCalendarBookingStates(form.values.statesOfInterest);
  const visibilityScopesValue = form.values.visibilityScopes;
  const customStateOptions = getCustomStateOptions(eventStates);
  const allowedCustomStateIds = customStateOptions.map(({ value }) => value);
  const customStatesOfInterestValue = eventStatesLoading
    ? form.values.customStatesOfInterest
    : form.values.customStatesOfInterest.filter((stateId) => allowedCustomStateIds.includes(stateId));
  const customFieldsInputProps = form.getInputProps('customFields');
  const statesOfInterestInputProps = form.getInputProps('statesOfInterest');
  const customStatesOfInterestInputProps = form.getInputProps('customStatesOfInterest');
  const kindsOfInterestInputProps = form.getInputProps('kindsOfInterest');

  const handleKindsOfInterestChange = (selectedKinds: string[]) => {
    kindsOfInterestInputProps.onChange(selectedKinds);
  };

  const toggleVisibilityScope = (scope: VisibilityScope) => {
    const isEnabled = visibilityScopesValue.includes(scope);
    let nextVisibilityScopes: VisibilityScope[];

    if (scope === VisibilityScope.LOCATION && isEnabled) {
      nextVisibilityScopes = visibilityScopesValue.filter(
        (visibilityScope) =>
          visibilityScope !== VisibilityScope.LOCATION && visibilityScope !== VisibilityScope.ONLY_LOCATION_CITY,
      );
    } else if (isEnabled) {
      nextVisibilityScopes = visibilityScopesValue.filter((visibilityScope) => visibilityScope !== scope);
    } else {
      nextVisibilityScopes = [...visibilityScopesValue, scope];
    }

    form.setFieldValue('visibilityScopes', nextVisibilityScopes);
  };

  useEffect(() => {
    setCustomAttributesLoading(true);
    CustomAttributeS.fetchAllAttributes()
      .then((attributes) => {
        setCustomAttributes(attributes);
        setCustomAttributesLoaded(true);
      })
      .catch(() => {
        setCustomAttributes([]);
        setCustomAttributesLoaded(false);
      })
      .finally(() => setCustomAttributesLoading(false));
  }, []);

  const onSubmit = () => {
    setLoading(true);
    GoogleS.editCalendarConfig(id, {
      ...form.values,
      statesOfInterest: statesOfInterestValue,
      customStatesOfInterest: customStatesOfInterestValue,
      customFields: customFieldsValue,
    })
      .then((calendar) => {
        if (calendar) {
          updateHandler(calendar);
          closeAllModals();
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Group>
        <Text>Kalender:</Text>
        <Group mb="md" mt="md">
          <Dot withoutShadow colour={backgroundColor} />
          <Text weight="bolder">{calendarName}</Text>
        </Group>
      </Group>
      <GrayBox>
        <Divider label={<ThemeText>Sichtbarkeit</ThemeText>} labelPosition="center" my="md" />
        {visibilityScopeOptions.map((option) => (
          <ConfigOption
            key={option.value}
            title={option.title}
            description={option.description}
            rightSection={
              <Switch
                checked={visibilityScopesValue.includes(option.value)}
                onChange={() => toggleVisibilityScope(option.value)}
              />
            }
          />
        ))}
        {visibilityScopesValue.includes(VisibilityScope.LOCATION) && (
          <ConfigOption
            title={onlyLocationCityVisibilityScopeOption.title}
            description={onlyLocationCityVisibilityScopeOption.description}
            rightSection={
              <Switch
                checked={visibilityScopesValue.includes(onlyLocationCityVisibilityScopeOption.value)}
                onChange={() => toggleVisibilityScope(onlyLocationCityVisibilityScopeOption.value)}
              />
            }
          />
        )}
        <MultiSelect
          mt="md"
          label={<ThemeText size="sm">Eigene Felder</ThemeText>}
          description="Ausgewählte eigene Felder werden in die Beschreibung der Kalendereinträge übernommen."
          placeholder="Eigene Felder auswählen"
          searchable
          clearable
          data={customAttributeOptions}
          value={customFieldsValue}
          disabled={customAttributesLoading}
          icon={customAttributesLoading && <Loader size="xs" />}
          rightSection={<IconChevronDown size={14} />}
          rightSectionWidth={25}
          onChange={(selectedFields) =>
            customFieldsInputProps.onChange(
              selectedFields.filter((fieldName) => allowedCustomFieldNames.includes(fieldName)),
            )
          }
          styles={{ rightSection: { pointerEvents: 'none' } }}
        />

        <Divider label={<ThemeText>Filter-Einstellungen</ThemeText>} labelPosition="center" my="md" />
        <MultiSelect
          label={<ThemeText size="sm">Buchungsstatus</ThemeText>}
          description="Nur Buchungen mit diesen Basis-Status werden in den Kalender eingetragen."
          placeholder="Status auswählen"
          searchable
          clearable
          data={bookingStateOptions}
          value={statesOfInterestValue}
          rightSection={<IconChevronDown size={14} />}
          rightSectionWidth={25}
          onChange={(selectedStates) =>
            statesOfInterestInputProps.onChange(filterCalendarBookingStates(selectedStates as BookingState[]))
          }
          styles={{ rightSection: { pointerEvents: 'none' } }}
        />
        <MultiSelect
          mt="md"
          label={<ThemeText size="sm">Eigene Buchungsstatus</ThemeText>}
          description="Optional kannst Du zusätzlich einzelne eigene Status auswählen."
          placeholder="Eigene Status auswählen"
          searchable
          clearable
          data={customStateOptions}
          value={customStatesOfInterestValue}
          disabled={eventStatesLoading}
          icon={eventStatesLoading && <Loader size="xs" />}
          rightSection={<IconChevronDown size={14} />}
          rightSectionWidth={25}
          onChange={(selectedStates) =>
            customStatesOfInterestInputProps.onChange(
              selectedStates.filter((stateId) => allowedCustomStateIds.includes(stateId)),
            )
          }
          styles={{ rightSection: { pointerEvents: 'none' } }}
        />
        <MultiSelect
          mt="md"
          label={<ThemeText size="sm">Auftragsarten</ThemeText>}
          description="Leer lassen, wenn alle Auftragsarten im Kalender erscheinen sollen."
          placeholder="Auftragsarten auswählen"
          searchable
          clearable
          data={DataS.getEventKindsOptions(kinds)}
          value={form.values.kindsOfInterest}
          disabled={eventKindsLoading}
          icon={eventKindsLoading && <Loader size="xs" />}
          rightSection={<IconChevronDown size={14} />}
          rightSectionWidth={25}
          onChange={handleKindsOfInterestChange}
          styles={{ rightSection: { pointerEvents: 'none' } }}
        />
      </GrayBox>
      <Center mt="xl">
        <Button sx={{ minWidth: 200 }} type="submit" disabled={loading || !form.isTouched()}>
          {i18n._('actions.save')}
        </Button>
      </Center>
    </form>
  );
};
